import { useEffect, useMemo, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { CaretUpDown, Check, CircleNotch } from "@phosphor-icons/react";

/**
 * Searchable brand picker.
 *
 * Two modes:
 *   1. Local  — pass `brands` (capped client filter, ≤100 visible)
 *   2. Remote — pass `onSearch(q) => Promise<string[]>`; results are
 *      fetched on demand. Empty query shows only "All Brands" (+ the
 *      currently selected value). Type ≥ `minChars` to search.
 *
 * `multiple` is opt-in. Without it the component behaves exactly as it
 * always has — `value` is a string, picking closes the popover — which
 * is what the four PubReports call sites rely on. With it, `value` is
 * an array, the popover stays open while ticking, and every selected
 * brand is pinned to the top of the list so it stays reachable while
 * the search box is filtering the rest away.
 */
const MAX_VISIBLE = 100;
const DEFAULT_MIN_CHARS = 2;

export default function BrandCombobox({
  value = "all",
  onChange,
  multiple = false,
  brands = [],
  onSearch = null,
  minChars = DEFAULT_MIN_CHARS,
  testId,
  placeholder = "All Brands",
  width = "w-56",
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [remoteBrands, setRemoteBrands] = useState([]);
  const [searching, setSearching] = useState(false);
  const reqId = useRef(0);
  const remote = typeof onSearch === "function";

  const selected = useMemo(() => {
    if (!multiple) return value && value !== "all" ? [value] : [];
    return Array.isArray(value) ? value : [];
  }, [multiple, value]);

  const label = selected.length === 0
    ? "All Brands"
    : selected.length === 1 ? selected[0] : `${selected.length} brands`;

  const pick = (brand) => {
    if (!multiple) {
      onChange(brand);
      setOpen(false);
      return;
    }
    onChange(
      selected.includes(brand)
        ? selected.filter((b) => b !== brand)
        : [...selected, brand],
    );
  };

  useEffect(() => {
    if (!remote || !open) return undefined;
    const q = search.trim();
    if (q.length < minChars) {
      setRemoteBrands([]);
      setSearching(false);
      return undefined;
    }
    const id = ++reqId.current;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const rows = await onSearch(q);
        if (reqId.current !== id) return;
        setRemoteBrands(Array.isArray(rows) ? rows.filter((b) => typeof b === "string") : []);
      } catch {
        if (reqId.current === id) setRemoteBrands([]);
      } finally {
        if (reqId.current === id) setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [remote, open, search, onSearch, minChars]);

  const { visible, isTruncated, matchCount, emptyHint } = useMemo(() => {
    if (remote) {
      const q = search.trim();
      let list = remoteBrands;
      // Keep selected brands visible even mid-search.
      const pinned = selected.filter((b) => !list.includes(b));
      if (pinned.length) list = [...pinned, ...list];
      if (q.length < minChars) {
        return {
          visible: selected,
          isTruncated: false,
          matchCount: 0,
          emptyHint: `Type at least ${minChars} characters to search`,
        };
      }
      return {
        visible: list.slice(0, MAX_VISIBLE),
        isTruncated: list.length > MAX_VISIBLE,
        matchCount: list.length,
        emptyHint: searching ? "Searching…" : "No brand found.",
      };
    }

    const list = Array.isArray(brands) ? brands.filter((b) => typeof b === "string") : [];
    const q = search.trim().toLowerCase();
    const filtered = q ? list.filter((b) => b.toLowerCase().includes(q)) : list;
    // Selected brands ride at the top so they never scroll out of reach
    // past the 100-row cap.
    const ordered = selected.length
      ? [...selected.filter((b) => filtered.includes(b)),
         ...filtered.filter((b) => !selected.includes(b))]
      : filtered;
    return {
      visible: ordered.slice(0, MAX_VISIBLE),
      isTruncated: filtered.length > MAX_VISIBLE,
      matchCount: filtered.length,
      emptyHint: "No brand found.",
    };
  }, [remote, remoteBrands, brands, search, selected, minChars, searching]);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setSearch("");
          setRemoteBrands([]);
          setSearching(false);
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`${width} h-9 justify-between font-normal text-sm`}
          data-testid={testId}
        >
          <span className="truncate text-left">{label || placeholder}</span>
          <CaretUpDown size={14} className="opacity-50 ml-2 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={remote ? "Type to search brands…" : "Search brand…"}
            value={search}
            onValueChange={setSearch}
            data-testid={testId ? `${testId}-search` : undefined}
          />
          <CommandList>
            <CommandEmpty>
              {searching ? (
                <span className="inline-flex items-center gap-1.5">
                  <CircleNotch size={12} className="animate-spin" /> Searching…
                </span>
              ) : emptyHint}
            </CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="all"
                onSelect={() => {
                  onChange(multiple ? [] : "all");
                  if (!multiple) setOpen(false);
                }}
                data-testid={testId ? `${testId}-option-all` : undefined}
              >
                <Check size={14} className={selected.length === 0 ? "opacity-100 mr-2" : "opacity-0 mr-2"} />
                All Brands
              </CommandItem>
              {visible.map((b) => (
                <CommandItem
                  key={b}
                  value={b}
                  onSelect={() => pick(b)}
                >
                  <Check size={14} className={selected.includes(b) ? "opacity-100 mr-2" : "opacity-0 mr-2"} />
                  {b}
                </CommandItem>
              ))}
            </CommandGroup>
            {isTruncated && (
              <div className="px-3 py-2 text-[11px] text-slate-400 border-t border-slate-100">
                Showing {MAX_VISIBLE} of {matchCount.toLocaleString()} — refine your search
              </div>
            )}
            {remote && search.trim().length < minChars && (
              <div className="px-3 py-2 text-[11px] text-slate-400 border-t border-slate-100">
                Type to search — brands are fetched on demand
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
